import { useEffect, useState } from 'react';
import { Category, Product } from '../types';
import { ProductCard } from '../components/catalog/ProductCard';
import { ArrowLeft } from 'lucide-react';

interface CategoryPageProps {
  slug: string;
  onNavigate: (path: string) => void;
}

export function CategoryPage({ slug, onNavigate }: CategoryPageProps) {
  const [category, setCategory] = useState<Category | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    Promise.all([
      fetch(`/api/categories/${slug}`).then((res) => {
        if (!res.ok) throw new Error('Categoria não encontrada.');
        return res.json();
      }),
      fetch(`/api/products?category=${slug}&limit=50`).then((res) => res.json()),
    ])
      .then(([catData, prodData]: [any, any]) => {
        setCategory(catData);
        setProducts(prodData.products || []);
      })
      .catch((err) => {
        setError(err.message || 'Erro ao carregar categoria.');
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 space-y-6">
        <div className="h-40 bg-surface-muted rounded-xs animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="aspect-[4/5] bg-surface-muted rounded-xs animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center space-y-4">
        <h2 className="text-2xl font-serif font-medium text-ink">Categoria não encontrada</h2>
        <p className="text-xs text-ink-muted">A categoria solicitada não existe ou foi arquivada.</p>
        <button
          onClick={() => onNavigate('/catalogo')}
          className="bg-ink text-ink-inverted px-6 py-2.5 rounded-xs text-xs uppercase tracking-wider font-medium hover:bg-primary transition-colors"
        >
          Voltar ao Catálogo
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-10">
      {/* Category Hero Header */}
      <div className="relative bg-background-alt border border-border-subtle rounded-xs p-8 sm:p-12 overflow-hidden">
        <button
          onClick={() => onNavigate('/catalogo')}
          className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink mb-6 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Voltar ao Catálogo
        </button>

        <div className="max-w-2xl space-y-3 relative z-10">
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-primary">
            Coleção Oficial
          </span>
          <h1 className="text-3xl sm:text-4xl font-serif font-normal text-ink">
            {category.name}
          </h1>
          {category.description && (
            <p className="text-xs sm:text-sm text-ink-muted leading-relaxed max-w-xl">
              {category.description}
            </p>
          )}
        </div>
      </div>

      {/* Products Grid */}
      <div>
        <div className="mb-6 flex items-center justify-between text-xs text-ink-muted">
          <span>{products.length} {products.length === 1 ? 'peça catalogada' : 'peças catalogadas'}</span>
        </div>

        {products.length === 0 ? (
          <div className="bg-surface border border-border-subtle rounded-xs p-12 text-center text-xs text-ink-muted">
            Nenhum produto publicado nesta categoria no momento.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} onNavigate={onNavigate} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
